import { apolloClient } from '@/vue-apollo'
import GetCurrentUser from '@/graphql/queries/GetCurrentUser'
import ListUsers from '@/graphql/queries/ListUsers'
import CreateUser from '@/graphql/mutations/CreateUser'
import UpdateUser from '@/graphql/mutations/UpdateUser'

const UserSortableField = {
  REGISTRATION_DATE: 'REGISTRATION_DATE',
  LAST_LOGIN: 'LAST_LOGIN',
  LOGIN_COUNTER: 'LOGIN_COUNTER'
}

const SortOrder = {
  ASC: 'ASC',
  DESC: 'DESC'
}

export const user = {
  namespaced: true,
  state: {
    user: null,
    users: null,
    nextTokenUsers: null,
    loadingUsers: false,
    searchTerm: '',
    sortBy: {
      field: UserSortableField.REGISTRATION_DATE,
      order: SortOrder.DESC
    }
  },
  mutations: {
    setUser (state, payload) {
      state.user = payload
    },
    updateUser (state, payload) {
      state.user = payload
    },
    setUsers (state, { items, append = false }) {
      if (append) {
        state.users = [...(state.users || []), ...items]
      } else {
        state.users = items
      }
    },
    setNextTokenUsers (state, token) {
      state.nextTokenUsers = token
    },
    setLoadingUsers (state, isLoading) {
      state.loadingUsers = isLoading
    },
    setCurrentSearchTerm (state, term) {
      state.searchTerm = term
    },
    setCurrentSortBy (state, sortBy) {
      state.sortBy = sortBy
    }
  },
  actions: {
    async createUser ({ commit }, input) {
      try {
        const res = await apolloClient.mutate({
          mutation: CreateUser,
          variables: {
            ...input
          }
        })
        commit('setUser', res.data.createUser)
      } catch (e) {
        commit('snackbar/showMessage', { content: 'An error occurred while creating user', timeout: 6000, color: 'red' }, { root: true })
      }
    },

    async updateUser ({ commit }, input) {
      try {
        const res = await apolloClient.mutate({
          mutation: UpdateUser,
          variables: {
            ...input
          }
        })
        commit('setUser', res.data.updateUser)
      } catch (e) {
        commit('snackbar/showMessage', { content: 'An error occurred while updating user', timeout: 6000, color: 'red' }, { root: true })
      }
    },

    async getCurrentUser ({ commit }) {
      const res = await apolloClient.query({
        query: GetCurrentUser,
        fetchPolicy: 'no-cache'
      })
      commit('setUser', res.data.getCurrentUser)
      return res
    },

    async listUsers (
      { commit, dispatch, state },
      {
        limit = 100,
        nextToken = null,
        search = null,
        sortBy = null,
        append = false,
        fetchAllForSearch = false
      } = {}
    ) {
      commit('setLoadingUsers', true)
      commit('setCurrentSortBy', sortBy)

      const isNewSearchInitiated = (search !== null && search !== state.searchTerm && !append)

      if (isNewSearchInitiated) {
        commit('setUsers', { items: [], append: false })
        commit('setNextTokenUsers', null)
      } else if (search !== null && !append && !fetchAllForSearch) {
        commit('setUsers', { items: [], append: false })
        commit('setNextTokenUsers', null)
      }

      commit('setCurrentSearchTerm', search || '')

      try {
        const variables = { limit, sortBy: state.sortBy }

        if (nextToken) {
          variables.nextToken = nextToken
        }
        if (search && search.trim() !== '') {
          variables.search = search.trim()
        }

        const res = await apolloClient.query({
          query: ListUsers,
          variables,
          fetchPolicy: 'no-cache'
        })

        const listUsersData = res.data.listUsers

        if (listUsersData) {
          commit('setUsers', { items: listUsersData.items, append: append || (fetchAllForSearch && nextToken) })
          commit('setNextTokenUsers', listUsersData.nextToken)

          if (fetchAllForSearch && listUsersData.nextToken) {
            await dispatch('listUsers', {
              limit,
              nextToken: listUsersData.nextToken,
              search,
              sortBy: state.sortBy,
              append: true,
              fetchAllForSearch: true
            })
          }
        }

        return res.data.listUsers
      } catch (error) {
        commit('snackbar/showMessage', {
          content: 'Error fetching users',
          timeout: 6000,
          color: 'red'
        }, { root: true })
        commit('setNextTokenUsers', null)
      } finally {
        if (!fetchAllForSearch || (fetchAllForSearch && !state.nextTokenUsers)) {
          commit('setLoadingUsers', false)
        }
      }
    }
  }
}

export { UserSortableField, SortOrder }
